import { useEffect, useState, useSyncExternalStore } from "react";

const isDark = () =>
  localStorage.theme === "dark" ||
  (!("theme" in localStorage) &&
    window.matchMedia("(prefers-color-scheme: dark)").matches);

/**
 * A theme imposed by the page rather than chosen by the viewer: the public chat
 * wears the tenant's (`branding.theme`), whatever the visitor's OS or a back-office
 * preference left in this origin's storage says. While it is set, the viewer's own
 * theme is remembered but not painted.
 */
let forcedTheme: "light" | "dark" | null = null;
let viewerTheme: "light" | "dark" = "light";

const paint = () => {
  const dark =
    forcedTheme === "dark" ||
    (!forcedTheme &&
      (viewerTheme === "dark" ||
        document.location.search.includes("theme=dark")));
  document.documentElement.classList.toggle("dark", dark);
};

/** Impose a theme on the page, or hand it back to the viewer with `null`. */
export const forceTheme = (theme: "light" | "dark" | null) => {
  forcedTheme = theme;
  paint();
};

export const useTheme = () => {
  const [theme, setTheme] = useState(isDark() ? "dark" : "light");

  useEffect(() => {
    viewerTheme = theme === "dark" ? "dark" : "light";
    paint();
  }, [theme]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const callback = (e: any) => {
      if (!localStorage.theme)
        if (e.matches) {
          setTheme("dark");
        } else {
          setTheme("light");
        }
    };
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", callback);
    return () =>
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .removeEventListener("change", callback);
  }, []);

  return {
    setTheme: (theme: "dark" | "light" | "") => {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";

      if (systemTheme === theme) theme = ""; // If we match the system, get back to system theme

      localStorage.theme = theme;
      setTheme(theme || systemTheme);
    },
    theme,
  };
};

const subscribeToThemeClass = (onChange: () => void) => {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  return () => observer.disconnect();
};

/**
 * Whether the page is drawn dark right now, read off the `dark` class that
 * `useTheme` sets. For what cannot follow the class through CSS — the toaster,
 * the charts' resolved colours. `useTheme` itself keeps its state per caller, so
 * a second instance would not see the user flip the theme from the sidebar;
 * the class is the one thing every screen agrees on.
 */
export const useIsDark = () =>
  useSyncExternalStore(
    subscribeToThemeClass,
    () => document.documentElement.classList.contains("dark"),
    () => false,
  );
